// Home "Tasks" view: one row per inline task (tasks/frontend-inline-tasks-phase-1-requirements.md) of
// the notes that pass the Home filters, with its own Open/Closed and due-date filters. Clicking a task
// opens its note in Edit with the cursor at the end of the task; the Completed cell toggles a task in
// place (click once to pick the cell, again to toggle); Select mode runs bulk actions on tasks.
//
// Changes made here go through DataService.updateNote like an edit in the note: the task blocks in the
// note's description are changed (applyInlineTaskChanges) and the note is saved locally first, then
// synced — so they work offline and need nothing new from the server.
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableModule } from 'primeng/table';
import { patchTableColumnResizeForZoom } from '../../../_util/table-resize-zoom.util';

import { DataService, type Note, type Tag } from '../../../_service/data.service';
import type { FilterParams } from '../../../_service/filter.service';
import type { Space } from '../../../_service/space.service';
import { AuthService } from '../../../_service/auth.service';
import { AlertService } from '../../../_service/alert.service';
import { SpaceDirectoryService } from '../../../_service/space-directory.service';
import { TaskAssigneeService } from '../../../_service/task-assignee.service';
import { FilterLogic, PrivateTagIndex } from '../logic/filter.logic';
import { getNoteDisplayTitle } from '../../../_util/note-display-title.util';
import {
  InlineTask,
  InlineTaskChange,
  TaskAssignee,
  TaskDueFilter,
  addDays,
  applyInlineTaskChanges,
  extractInlineTasksFromHtml,
  formatTaskDueDate,
  formatTaskDueDateLong,
  isTaskOverdue,
  isValidTaskDueDate,
  mergeInlineTasks,
  noteInlineTasks,
  taskMatchesDueFilter,
  taskOwnerName,
  toTaskDueDate,
} from '../../../_util/inline-task.util';
import { NOTE_FOCUS_TASK_STATE_KEY } from '../../note/note-task-focus';

export type TaskSortField = 'Description' | 'Note' | 'Completed' | 'Owner' | 'Due';
export type TaskSortOrder = 'asc' | 'desc';
export type TaskStatusFilter = 'all' | 'open' | 'closed';

export interface TaskRow {
  /** Unique per row: note id + task id (a task id is unique within its note). */
  key: string;
  task: InlineTask;
  note: Note;
  /** Position of the task in its note (keeps a note's tasks in document order on ties). */
  index: number;
  noteTitle: string;
  ownerName: string;
  overdue: boolean;
  dueLabel: string;
  dueLong: string;
}

export const TASK_SORT_STORAGE_KEY = 'notesapp.home.tasks.sort';
/** The Tasks view's own Open/Closed and due-date filters (per device, like the sort). */
export const TASK_FILTER_STORAGE_KEY = 'notesapp.home.tasks.filters';
/** PrimeNG table state (column widths) of the desktop Tasks table. */
export const TASK_TABLE_STATE_KEY = 'notesapp.home.tasks.desktop.v1';
export const TASK_SORT_FIELDS: readonly TaskSortField[] = ['Due', 'Description', 'Note', 'Completed', 'Owner'];
const DEFAULT_SORT: { field: TaskSortField; order: TaskSortOrder } = { field: 'Due', order: 'asc' };

export const TASK_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: TaskStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];
export const TASK_DUE_FILTER_OPTIONS: ReadonlyArray<{ value: TaskDueFilter; label: string }> = [
  { value: 'any', label: 'Due anytime' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: 'tomorrow', label: 'Due tomorrow' },
  { value: 'week', label: 'Due this week' },
];

export type TaskBulkAction = 'complete' | 'reopen' | 'assign-me' | 'due-date' | 'delete';

const text = (value: string) => value.toLocaleLowerCase();

/** Sorts rows by a column. Empty values (no owner, no due date) always go last, whatever the
 * direction; ties fall back to note title, then the task's position in its note. */
export function sortTaskRows(rows: TaskRow[], field: TaskSortField, order: TaskSortOrder): TaskRow[] {
  const dir = order === 'asc' ? 1 : -1;
  const keyOf = (row: TaskRow): string | number | null => {
    switch (field) {
      case 'Description':
        return text(row.task.description);
      case 'Note':
        return text(row.noteTitle);
      case 'Completed':
        return row.task.completed ? 1 : 0;
      case 'Owner':
        return row.task.ownerId ? text(row.ownerName) : null;
      case 'Due':
        return isValidTaskDueDate(row.task.dueDate) ? row.task.dueDate! : null;
    }
  };
  const tieBreak = (a: TaskRow, b: TaskRow) =>
    a.noteTitle.localeCompare(b.noteTitle, undefined, { sensitivity: 'base' }) || a.note._id.localeCompare(b.note._id) || a.index - b.index;
  return [...rows].sort((a, b) => {
    const ka = keyOf(a);
    const kb = keyOf(b);
    if (ka === null || kb === null) {
      if (ka !== kb) return ka === null ? 1 : -1;
      return tieBreak(a, b);
    }
    const cmp = typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb));
    return cmp * dir || tieBreak(a, b);
  });
}

/** Whether a task passes the Tasks view's own filters. */
export function taskPassesViewFilters(task: InlineTask, status: TaskStatusFilter, due: TaskDueFilter, today: Date = new Date()): boolean {
  if (status === 'open' && task.completed) return false;
  if (status === 'closed' && !task.completed) return false;
  return taskMatchesDueFilter(task, due, today);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

@Component({
  selector: 'app-task-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    DialogModule,
    MenuModule,
    SelectModule,
    SelectButtonModule,
    TableModule,
  ],
  templateUrl: './task-list.component.html',
  // The List view's table styles (header colours, gridlines, compact cells), so both tables match.
  styleUrl: '../list/list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskListComponent implements OnInit, OnChanges, OnDestroy {
  /** Notes that passed the Home filters. */
  @Input() notes: Note[] = [];
  @Input() filter: FilterParams = {};
  @Input() tags: Tag[] = [];
  @Input() privateTagIndex: PrivateTagIndex = null;
  /** The user's Spaces (a task in a Space the user can only view can't be changed here). */
  @Input() spaces: Space[] = [];
  /** The notes are still being read: show "Loading tasks…" rather than "No tasks". */
  @Input() loading = false;
  @Input() isSelectMode = false;
  @Output() exitSelectMode = new EventEmitter<void>();

  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly assigneeService = inject(TaskAssigneeService);
  private readonly dataService = inject(DataService);
  private readonly auth = inject(AuthService);
  private readonly alerts = inject(AlertService);
  private readonly spaceDirectory = inject(SpaceDirectoryService);

  /** Every task of the notes (before the view's own filters). */
  private allRows: TaskRow[] = [];
  /** The rows shown: the view's filters applied, sorted. */
  rows: TaskRow[] = [];
  readonly columns: readonly TaskSortField[] = ['Description', 'Note', 'Completed', 'Owner', 'Due'];
  readonly desktopTableStateKey = TASK_TABLE_STATE_KEY;
  /** Starting widths (cells truncate, so without these the Task column would shrink to nothing);
   * widths the user drags to are saved and take over. */
  readonly defaultColumnWidths: Record<TaskSortField, string> = { Description: '38%', Note: '24%', Completed: '10%', Owner: '14%', Due: '14%' };
  readonly selectColumnStyle = 'width: 2.25rem !important; min-width: 2.25rem !important; max-width: 2.25rem !important';
  /** Set briefly after a column resize, so the mouse-up isn't taken as a click that sorts. */
  private isResizing = false;

  @ViewChild('desktopTable')
  set desktopTable(table: Table | undefined) {
    queueMicrotask(() => patchTableColumnResizeForZoom(table));
  }

  /** The print-only sheet is moved to <body>, so a print shows just it (styles.scss hides the rest). */
  @ViewChild('printSheet')
  set printSheet(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement;
    if (el && typeof document !== 'undefined' && el.parentElement !== document.body) {
      this.printSheetEl = el;
      document.body.appendChild(el);
    }
  }
  private printSheetEl: HTMLElement | null = null;

  sortBy: TaskSortField = DEFAULT_SORT.field;
  sortOrder: TaskSortOrder = DEFAULT_SORT.order;
  readonly columnLabel = (field: TaskSortField) => TaskListComponent.columnLabel(field);
  readonly sortOptions = TASK_SORT_FIELDS.map((value) => ({ value, label: TaskListComponent.columnLabel(value) }));

  // ---- The view's filters ----
  statusFilter: TaskStatusFilter = 'all';
  dueFilter: TaskDueFilter = 'any';
  readonly statusFilterOptions = [...TASK_STATUS_FILTER_OPTIONS];
  readonly dueFilterOptions = [...TASK_DUE_FILTER_OPTIONS];

  // ---- Completed cell: first click picks the cell, the next one toggles ----
  activeCompletedKey: string | null = null;

  // ---- Select mode ----
  readonly selectedKeys = new Set<string>();
  readonly bulkMenuItems: MenuItem[] = [
    { label: 'Set to Completed', icon: 'pi pi-check-square', command: () => this.runBulkAction('complete') },
    { label: 'Set to Not Completed', icon: 'pi pi-stop', command: () => this.runBulkAction('reopen') },
    { label: 'Assign to me', icon: 'pi pi-user', command: () => this.runBulkAction('assign-me') },
    { label: 'Change due date…', icon: 'pi pi-calendar', command: () => this.openDueDateDialog() },
    { separator: true },
    { label: 'Delete tasks…', icon: 'pi pi-trash', styleClass: 'task-bulk-delete', command: () => (this.deleteDialogVisible = true) },
  ];
  dueDateDialogVisible = false;
  dueDateValue: Date | null = null;
  deleteDialogVisible = false;
  busy = false;

  /** Tailwind's `lg` breakpoint, as in the List view: table on large screens, card rows otherwise. */
  private static readonly LARGE_SCREEN_QUERY = '(min-width: 64rem)';
  isLargeScreen = true;
  private largeScreenMql?: MediaQueryList;
  private readonly onLargeScreenChange = (event: MediaQueryListEvent) => {
    this.isLargeScreen = event.matches;
    this.cdr.markForCheck();
  };

  /** Member names per Space (for the Owner column), loaded once per Space that has owned tasks. */
  private readonly assigneesBySpace = new Map<string, TaskAssignee[]>();
  private readonly requestedSpaces = new Set<string>();
  private readonly subscriptions = new Subscription();

  static columnLabel(field: TaskSortField): string {
    return field === 'Due' ? 'Due date' : field === 'Note' ? 'Note title' : field === 'Description' ? 'Task' : field;
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.largeScreenMql = window.matchMedia(TaskListComponent.LARGE_SCREEN_QUERY);
      this.isLargeScreen = this.largeScreenMql.matches;
      this.largeScreenMql.addEventListener('change', this.onLargeScreenChange);
    }
    this.loadPersistedSort();
    this.loadPersistedFilters();
    this.rebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSelectMode'] && !this.isSelectMode) this.selectedKeys.clear();
    if (changes['notes'] || changes['filter'] || changes['tags'] || changes['privateTagIndex']) this.rebuild();
    else this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.largeScreenMql?.removeEventListener('change', this.onLargeScreenChange);
    this.subscriptions.unsubscribe();
    this.printSheetEl?.remove();
  }

  get countLabel(): string {
    const open = this.rows.filter((r) => !r.task.completed).length;
    const label = `${this.rows.length} task${this.rows.length === 1 ? '' : 's'} · ${open} open`;
    return this.rows.length !== this.allRows.length ? `${label} (of ${this.allRows.length})` : label;
  }

  /** "No tasks" when the notes have none; otherwise a hint that the view's filters hide them. */
  get emptyMessage(): string {
    return this.allRows.length ? 'No tasks match the filters.' : 'No tasks to show.';
  }

  get filtersActive(): boolean {
    return this.statusFilter !== 'all' || this.dueFilter !== 'any';
  }

  // ---- Rows ----

  private rebuild(): void {
    const rows: TaskRow[] = [];
    const today = new Date();
    for (const note of this.notes ?? []) {
      if (note.syncStatus === 'deleted') continue;
      const tasks = noteInlineTasks(note);
      if (!tasks.length) continue;
      const noteTitle = getNoteDisplayTitle(note.Title, note.DescriptionHtml);
      tasks.forEach((task, index) => {
        if (!FilterLogic.taskMatchesSearch(task, note, this.filter, this.tags, this.privateTagIndex)) return;
        if (task.ownerId) this.ensureAssignees(note.spaceId);
        rows.push({
          key: `${note._id}::${task.id}`,
          task,
          note,
          index,
          noteTitle,
          ownerName: taskOwnerName(task.ownerId, this.assigneesFor(note.spaceId)),
          overdue: isTaskOverdue(task, today),
          dueLabel: task.dueDate ? formatTaskDueDate(task.dueDate, today) : '',
          dueLong: task.dueDate ? formatTaskDueDateLong(task.dueDate) : '',
        });
      });
    }
    this.allRows = rows;
    this.applyViewFilters();
  }

  /** Applies the Open/Closed and due-date filters, sorts, and drops selections that are no longer shown. */
  private applyViewFilters(): void {
    const today = new Date();
    const shown = this.allRows.filter((row) => taskPassesViewFilters(row.task, this.statusFilter, this.dueFilter, today));
    this.rows = sortTaskRows(shown, this.sortBy, this.sortOrder);
    const keys = new Set(this.rows.map((r) => r.key));
    for (const key of [...this.selectedKeys]) if (!keys.has(key)) this.selectedKeys.delete(key);
    if (this.activeCompletedKey && !keys.has(this.activeCompletedKey)) this.activeCompletedKey = null;
    this.cdr.markForCheck();
  }

  private assigneesFor(spaceId: string | undefined): TaskAssignee[] | null {
    const id = this.assigneeService.resolveSpaceId(spaceId);
    return id ? this.assigneesBySpace.get(id) ?? null : null;
  }

  private ensureAssignees(spaceId: string | undefined): void {
    const id = this.assigneeService.resolveSpaceId(spaceId);
    if (!id || this.requestedSpaces.has(id)) return;
    this.requestedSpaces.add(id);
    this.subscriptions.add(
      this.assigneeService.assignees$(id).subscribe((list) => {
        if (!list.assignees) return;
        this.assigneesBySpace.set(id, list.assignees);
        // Owner names arrived: refresh the rows (asynchronously — never during the current rebuild).
        queueMicrotask(() => this.rebuild());
      }),
    );
  }

  trackRow = (_: number, row: TaskRow) => row.key;

  // ---- The view's filters ----

  setStatusFilter(value: TaskStatusFilter | null): void {
    // The SelectButton emits null when the active option is clicked again: keep it.
    if (!value || !TASK_STATUS_FILTER_OPTIONS.some((o) => o.value === value)) {
      this.cdr.markForCheck();
      return;
    }
    this.statusFilter = value;
    this.persistFilters();
    this.applyViewFilters();
  }

  setDueFilter(value: TaskDueFilter | null): void {
    this.dueFilter = value && TASK_DUE_FILTER_OPTIONS.some((o) => o.value === value) ? value : 'any';
    this.persistFilters();
    this.applyViewFilters();
  }

  private persistFilters(): void {
    try {
      localStorage.setItem(TASK_FILTER_STORAGE_KEY, JSON.stringify({ status: this.statusFilter, due: this.dueFilter }));
    } catch {
      // Storage unavailable: the filters just aren't remembered.
    }
  }

  private loadPersistedFilters(): void {
    try {
      const saved = JSON.parse(localStorage.getItem(TASK_FILTER_STORAGE_KEY) ?? 'null');
      if (saved && TASK_STATUS_FILTER_OPTIONS.some((o) => o.value === saved.status)) this.statusFilter = saved.status;
      if (saved && TASK_DUE_FILTER_OPTIONS.some((o) => o.value === saved.due)) this.dueFilter = saved.due;
    } catch {
      // Missing/garbled: defaults.
    }
  }

  // ---- Sorting ----

  sortData(field: TaskSortField): void {
    if (this.isResizing) return;
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'asc';
    }
    this.applySort();
  }

  onColumnResizeEnd(): void {
    this.isResizing = true;
    setTimeout(() => (this.isResizing = false), 100);
  }

  setSortField(field: TaskSortField): void {
    if (!TASK_SORT_FIELDS.includes(field)) return;
    this.sortBy = field;
    this.applySort();
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.applySort();
  }

  ariaSort(field: TaskSortField): 'ascending' | 'descending' | 'none' {
    return this.sortBy === field ? (this.sortOrder === 'asc' ? 'ascending' : 'descending') : 'none';
  }

  private applySort(): void {
    this.rows = sortTaskRows(this.rows, this.sortBy, this.sortOrder);
    try {
      localStorage.setItem(TASK_SORT_STORAGE_KEY, JSON.stringify({ field: this.sortBy, order: this.sortOrder }));
    } catch {
      // Storage unavailable: the sort just isn't remembered.
    }
    this.cdr.markForCheck();
  }

  private loadPersistedSort(): void {
    try {
      const saved = JSON.parse(localStorage.getItem(TASK_SORT_STORAGE_KEY) ?? 'null');
      if (saved && TASK_SORT_FIELDS.includes(saved.field)) this.sortBy = saved.field;
      if (saved && (saved.order === 'asc' || saved.order === 'desc')) this.sortOrder = saved.order;
    } catch {
      // Missing/garbled: defaults.
    }
  }

  // ---- Row clicks ----

  /** A row click: in Select mode it (de)selects the task; otherwise it opens the note in Edit with the
   * cursor at the end of the task. Back/Close on that page returns to this view. */
  onRowClick(row: TaskRow): void {
    if (this.isSelectMode) {
      this.toggleSelected(row);
      return;
    }
    this.openRow(row);
  }

  openRow(row: TaskRow): void {
    this.router.navigate(['edit', row.note._id], {
      state: { filter: this.filter, homeState: { view: 'Tasks' }, [NOTE_FOCUS_TASK_STATE_KEY]: row.task.id },
    });
  }

  /** The Completed cell: the first click picks the cell (highlighted), a click on the picked cell
   * toggles the task between Open and Done. */
  onCompletedCellClick(row: TaskRow, event: Event): void {
    event.stopPropagation();
    if (this.activeCompletedKey !== row.key) {
      this.activeCompletedKey = row.key;
      this.cdr.markForCheck();
      return;
    }
    void this.toggleCompleted(row);
  }

  /** A click anywhere else un-picks the Completed cell. */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.activeCompletedKey) {
      this.activeCompletedKey = null;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.onDocumentClick();
  }

  completedCellTitle(row: TaskRow): string {
    if (this.activeCompletedKey === row.key) return row.task.completed ? 'Click again to reopen the task' : 'Click again to mark the task Done';
    return 'Click to select, click again to toggle Done / Open';
  }

  async toggleCompleted(row: TaskRow): Promise<void> {
    if (!this.canEdit(row.note)) {
      this.alerts.warn('This task is in a note you can only view.');
      return;
    }
    const completed = !row.task.completed;
    // Shown at once; the saved note (a moment later) rebuilds the rows from its HTML.
    this.patchRowLocally(row, { completed });
    await this.applyChanges([{ row, change: { completed } }]);
  }

  private patchRowLocally(row: TaskRow, change: InlineTaskChange): void {
    const task: InlineTask = { ...row.task };
    if (change.completed !== undefined) task.completed = change.completed;
    if (change.dueDate !== undefined) task.dueDate = change.dueDate ?? undefined;
    const updated: TaskRow = {
      ...row,
      task,
      overdue: isTaskOverdue(task),
      dueLabel: task.dueDate ? formatTaskDueDate(task.dueDate) : '',
      dueLong: task.dueDate ? formatTaskDueDateLong(task.dueDate) : '',
    };
    this.allRows = this.allRows.map((r) => (r.key === row.key ? updated : r));
    this.rows = this.rows.map((r) => (r.key === row.key ? updated : r));
    this.cdr.markForCheck();
  }

  // ---- Select mode ----

  isSelected(row: TaskRow): boolean {
    return this.selectedKeys.has(row.key);
  }

  toggleSelected(row: TaskRow, selected: boolean = !this.selectedKeys.has(row.key)): void {
    if (selected) this.selectedKeys.add(row.key);
    else this.selectedKeys.delete(row.key);
    this.cdr.markForCheck();
  }

  get selectedCount(): number {
    return this.selectedKeys.size;
  }

  get allShownSelected(): boolean {
    return this.rows.length > 0 && this.rows.every((r) => this.selectedKeys.has(r.key));
  }

  selectAllShown(): void {
    for (const row of this.rows) this.selectedKeys.add(row.key);
    this.cdr.markForCheck();
  }

  clearSelection(): void {
    this.selectedKeys.clear();
    this.cdr.markForCheck();
  }

  private get selectedRows(): TaskRow[] {
    return this.rows.filter((r) => this.selectedKeys.has(r.key));
  }

  openDueDateDialog(): void {
    const dates = [...new Set(this.selectedRows.map((r) => r.task.dueDate ?? ''))];
    // All selected tasks share a due date: start from it.
    this.dueDateValue = dates.length === 1 && dates[0] ? new Date(`${dates[0]}T00:00:00`) : null;
    this.dueDateDialogVisible = true;
    this.cdr.markForCheck();
  }

  setDueDateQuick(when: 'today' | 'tomorrow' | 'next-week'): void {
    const today = new Date();
    this.dueDateValue = addDays(today, when === 'today' ? 0 : when === 'tomorrow' ? 1 : 7);
    this.cdr.markForCheck();
  }

  async applyDueDate(clear = false): Promise<void> {
    const dueDate = clear ? null : toTaskDueDate(this.dueDateValue);
    if (!clear && !dueDate) return;
    this.dueDateDialogVisible = false;
    await this.runBulkAction('due-date', dueDate);
  }

  async confirmDelete(): Promise<void> {
    this.deleteDialogVisible = false;
    await this.runBulkAction('delete');
  }

  /** Runs a bulk action on the selected tasks (all of them that are shown). */
  async runBulkAction(action: TaskBulkAction, dueDate: string | null = null): Promise<void> {
    const rows = this.selectedRows;
    if (!rows.length || this.busy) return;

    let change: InlineTaskChange;
    let targets = rows;
    switch (action) {
      case 'complete':
        change = { completed: true };
        targets = rows.filter((r) => !r.task.completed);
        break;
      case 'reopen':
        change = { completed: false };
        targets = rows.filter((r) => r.task.completed);
        break;
      case 'assign-me': {
        const me = this.currentUserId();
        if (!me) {
          this.alerts.warn('Your user account isn\'t known on this device yet — connect once, then try again.');
          return;
        }
        change = { ownerId: me };
        targets = rows.filter((r) => r.task.ownerId !== me);
        break;
      }
      case 'due-date':
        change = { dueDate };
        targets = rows.filter((r) => (r.task.dueDate ?? null) !== dueDate);
        break;
      case 'delete':
        change = { delete: true };
        break;
    }

    const result = await this.applyChanges(targets.map((row) => ({ row, change })));
    const unchanged = rows.length - targets.length;
    const done = this.describeBulkResult(action, result.changed);
    const extra = [
      unchanged ? `${unchanged} already ${this.alreadyLabel(action)}` : '',
      result.readOnly ? `${result.readOnly} in notes you can only view` : '',
      result.failed ? `${result.failed} could not be saved` : '',
    ].filter(Boolean);
    const message = extra.length ? `${done} (${extra.join(', ')}).` : `${done}.`;
    if (result.failed || (result.readOnly && !result.changed)) this.alerts.warn(message);
    else this.alerts.success(message);
    if (action === 'delete') this.clearSelection();
  }

  private describeBulkResult(action: TaskBulkAction, count: number): string {
    const tasks = `${count} task${count === 1 ? '' : 's'}`;
    switch (action) {
      case 'complete':
        return `${tasks} set to Completed`;
      case 'reopen':
        return `${tasks} set to Not Completed`;
      case 'assign-me':
        return `${tasks} assigned to you`;
      case 'due-date':
        return `Due date changed for ${tasks}`;
      case 'delete':
        return `${tasks} deleted`;
    }
  }

  private alreadyLabel(action: TaskBulkAction): string {
    return action === 'complete' ? 'completed' : action === 'reopen' ? 'open' : action === 'assign-me' ? 'yours' : 'set';
  }

  /** The signed-in user's id: the owner of their private Space, else their entry in a loaded member list. */
  private currentUserId(): string | null {
    const username = this.auth.getUsername();
    const spaces = this.spaces?.length ? this.spaces : this.spaceDirectory.spaces;
    const privateSpace = spaces.find(
      (s) => s.type === 'PRIVATE' && (s.current_user_role === 'OWNER' || (!!username && s.owner_username === username)),
    );
    if (privateSpace?.owner_user_id) return privateSpace.owner_user_id;
    if (!username) return null;
    for (const list of this.assigneesBySpace.values()) {
      const me = list.find((a) => a.name === username);
      if (me) return me.id;
    }
    return null;
  }

  private canEdit(note: Note): boolean {
    const space = note.spaceId ? this.spaces?.find((s) => s.id === note.spaceId) : null;
    return !space || (space.current_user_role !== 'VIEWER' && space.status !== 'ARCHIVED');
  }

  /** Saves task changes into their notes (one save per note), offline-first. */
  private async applyChanges(items: Array<{ row: TaskRow; change: InlineTaskChange }>): Promise<{ changed: number; readOnly: number; failed: number }> {
    const result = { changed: 0, readOnly: 0, failed: 0 };
    const byNote = new Map<string, { note: Note; changes: Map<string, InlineTaskChange> }>();
    for (const { row, change } of items) {
      if (!this.canEdit(row.note)) {
        result.readOnly++;
        continue;
      }
      const entry = byNote.get(row.note._id) ?? { note: row.note, changes: new Map<string, InlineTaskChange>() };
      entry.changes.set(row.task.id, change);
      byNote.set(row.note._id, entry);
    }
    if (!byNote.size) {
      this.rebuild(); // undo any optimistic change
      return result;
    }

    this.busy = true;
    this.cdr.markForCheck();
    const now = new Date().toISOString();
    try {
      for (const { note, changes } of byNote.values()) {
        // The latest copy of the note (it may have been saved/synced since this list was built).
        const latest = this.dataService.allNotes.find((n) => n?._id === note._id) ?? note;
        const html = applyInlineTaskChanges(latest.DescriptionHtml, changes, now);
        if (html === (latest.DescriptionHtml ?? '')) continue;
        const updated: Note = { ...latest, DescriptionHtml: html };
        const tasks = mergeInlineTasks(extractInlineTasksFromHtml(html, now), latest.tasks as InlineTask[] | undefined);
        if (tasks.length || latest.tasks !== undefined) updated.tasks = tasks;
        try {
          const response = await firstValueFrom(this.dataService.updateNote(updated));
          if (response.status === 'error') throw new Error(response.error?.message);
          result.changed += changes.size;
        } catch (err) {
          console.error('Tasks view: saving', note._id, 'failed', err);
          result.failed += changes.size;
        }
      }
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
    if (result.failed) this.rebuild();
    return result;
  }

  // ---- Print ----

  /** One line under the printed title: which tasks, from which search. */
  get printSummary(): string {
    const parts = [
      this.statusFilter === 'all' ? 'All tasks' : this.statusFilter === 'open' ? 'Open tasks' : 'Closed tasks',
      TASK_DUE_FILTER_OPTIONS.find((o) => o.value === this.dueFilter)?.label ?? '',
    ];
    const search = (this.filter?.SearchString ?? '').trim();
    if (search) parts.push(`Search: "${search}"`);
    parts.push(this.countLabel);
    return parts.filter(Boolean).join(' · ');
  }

  get printedAt(): string {
    const d = new Date();
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }

  /** Printed due date: DD.MM.YYYY (relative words like "Tomorrow" age badly on paper). */
  printDue(row: TaskRow): string {
    const due = row.task.dueDate;
    if (!isValidTaskDueDate(due)) return '';
    const [y, m, d] = due.split('-');
    return `${d}.${m}.${y}`;
  }
}
